import { useState, useEffect, useMemo } from 'react'
import { api } from '../lib/api'
import { FacilitatorGuard } from '../components/FacilitatorGuard'
import AdminNav from '../components/AdminNav'
import {
  DownloadSimple,
  Faders,
  X,
  ChartBar,
  Users,
  CheckCircle,
  Target,
} from '@phosphor-icons/react'

interface ModuleStat {
  moduleId: string
  total: number
  completed: number
  inProgress: number
  notStarted: number
  avgPercent: number
}

interface CrewStat {
  crew: string
  team: string
  role: string
  totalUsers: number
  completedModules: number
}

interface OverviewData {
  conventionId: string
  totalTrainees: number
  overall: {
    totalProgressRows: number
    completedRows: number
    avgPercent: number
  }
  modules: ModuleStat[]
  crewBreakdown: CrewStat[]
  aggregate: {
    totalCompletions: number
    totalModules: number
    averageCompletionRate: number
  }
}

const CREW_OPTIONS = ['audio', 'video', 'stage', 'it']
const TEAM_OPTIONS = ['installation', 'operations']

function StatCard({
  label,
  value,
  sub,
  icon,
}: {
  label: string
  value: string
  sub?: string
  icon?: React.ReactNode
}) {
  return (
    <div className="stat-card">
      <div className="stat-card__header">
        {icon && <div className="stat-card__icon">{icon}</div>}
      </div>
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__label">{label}</div>
      {sub && <div className="stat-card__sub">{sub}</div>}
    </div>
  )
}

function CompletionBar({ label, value, total, color }: { label: string; value: number; total: number; color?: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <div className="completion-bar">
      <div className="completion-bar__label">
        <span>{label}</span>
        <span>{value} / {total} ({pct}%)</span>
      </div>
      <div className="completion-bar__track">
        <div
          className="completion-bar__fill"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  )
}

function ModuleHeatmap({ modules }: { modules: ModuleStat[] }) {
  const maxTotal = Math.max(...modules.map((m) => m.total), 1)

  return (
    <div className="heatmap">
      <div className="heatmap__header">
        <span>Module</span>
        <span>Completed</span>
        <span>In Progress</span>
        <span>Not Started</span>
      </div>
      {modules.map((m) => (
        <div key={m.moduleId} className="heatmap__row">
          <span className="heatmap__label" title={m.moduleId}>{m.moduleId}</span>
          <div className="heatmap__cells">
            <div
              className="heatmap__cell heatmap__cell--completed"
              style={{ opacity: Math.max(0.15, m.completed / maxTotal) }}
              title={`Completed: ${m.completed}`}
            />
            <div
              className="heatmap__cell heatmap__cell--inprogress"
              style={{ opacity: Math.max(0.15, m.inProgress / maxTotal) }}
              title={`In Progress: ${m.inProgress}`}
            />
            <div
              className="heatmap__cell heatmap__cell--notstarted"
              style={{ opacity: Math.max(0.15, m.notStarted / maxTotal) }}
              title={`Not Started: ${m.notStarted}`}
            />
          </div>
          <span className="heatmap__pct">{m.avgPercent}%</span>
        </div>
      ))}
    </div>
  )
}

function CrewChart({ crewBreakdown }: { crewBreakdown: CrewStat[] }) {
  const byCrew = useMemo(() => {
    const map = new Map<string, { users: number; completions: number }>()
    for (const c of crewBreakdown) {
      const existing = map.get(c.crew) || { users: 0, completions: 0 }
      existing.users += c.totalUsers
      existing.completions += c.completedModules
      map.set(c.crew, existing)
    }
    return Array.from(map.entries()).map(([crew, data]) => ({ crew, ...data }))
  }, [crewBreakdown])

  return (
    <div className="crew-chart">
      {byCrew.map((c) => {
        const pct = Math.round((c.completions / Math.max(c.users * 5, 1)) * 100)
        return (
          <div key={c.crew} className="crew-chart__bar">
            <div className="crew-chart__label">
              <span className={`badge badge--${c.crew}`}>{c.crew}</span>
              <span>{c.users} users</span>
            </div>
            <div className="crew-chart__track">
              <div
                className="crew-chart__fill"
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
            <span className="crew-chart__value">{pct}% est.</span>
          </div>
        )
      })}
    </div>
  )
}

function exportToCSV(data: OverviewData) {
  const rows = [
    ['Module', 'Total', 'Completed', 'In Progress', 'Not Started', 'Avg Percent'],
    ...data.modules.map((m) => [
      m.moduleId,
      String(m.total),
      String(m.completed),
      String(m.inProgress),
      String(m.notStarted),
      `${m.avgPercent}%`,
    ]),
    [],
    ['Crew', 'Team', 'Role', 'Users', 'Completions'],
    ...data.crewBreakdown.map((c) => [
      c.crew,
      c.team,
      c.role,
      String(c.totalUsers),
      String(c.completedModules),
    ]),
    [],
    ['Aggregate Stats'],
    ['Total Trainees', String(data.totalTrainees)],
    ['Total Completions', String(data.aggregate.totalCompletions)],
    ['Overall Completion Rate', `${data.aggregate.averageCompletionRate}%`],
    ['Overall Avg Percent', `${data.overall.avgPercent}%`],
  ]

  const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `progress-overview-${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function ProgressOverview() {
  const [data, setData] = useState<OverviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ crew: '', team: '', role: '' })
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const result = await api.getProgressOverview({
          ...(filters.crew && { crew: filters.crew }),
          ...(filters.team && { team: filters.team }),
          ...(filters.role && { role: filters.role }),
        })
        setData(result)
      } catch (err) {
        console.error('Failed to load progress overview:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [filters])

  if (loading) return <p>Loading progress data…</p>
  if (!data) return <p className="error">Failed to load progress data.</p>

  const overallRate = data.overall.totalProgressRows > 0
    ? Math.round((data.overall.completedRows / data.overall.totalProgressRows) * 100)
    : 0

  return (
    <div>
      <div className="progress-toolbar">
        <button
          className={`btn btn-secondary btn-sm ${showFilters ? 'btn--active' : ''}`}
          onClick={() => setShowFilters((v) => !v)}
        >
          <Faders size={16} /> Filters
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => exportToCSV(data)}>
          <DownloadSimple size={16} /> Export CSV
        </button>
      </div>

      {showFilters && (
        <div className="progress-filters">
          <div className="filter-group">
            <label>Crew</label>
            <select value={filters.crew} onChange={(e) => setFilters((f) => ({ ...f, crew: e.target.value }))}>
              <option value="">All crews</option>
              {CREW_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Team</label>
            <select value={filters.team} onChange={(e) => setFilters((f) => ({ ...f, team: e.target.value }))}>
              <option value="">All teams</option>
              {TEAM_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Role</label>
            <input
              type="text"
              value={filters.role}
              onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
              placeholder="e.g. td, switcher"
            />
          </div>
          <button className="btn btn-sm" onClick={() => setFilters({ crew: '', team: '', role: '' })}>
            <X size={14} /> Clear
          </button>
        </div>
      )}

      <div className="stat-cards">
        <StatCard
          label="Trainees"
          value={String(data.totalTrainees)}
          icon={<Users size={20} />}
        />
        <StatCard
          label="Overall Completion"
          value={`${overallRate}%`}
          sub={`${data.overall.completedRows} / ${data.overall.totalProgressRows} modules`}
          icon={<CheckCircle size={20} />}
        />
        <StatCard
          label="Avg Progress"
          value={`${data.overall.avgPercent}%`}
          icon={<Target size={20} />}
        />
        <StatCard
          label="Total Completions"
          value={String(data.aggregate.totalCompletions)}
          sub={`across ${data.aggregate.totalModules} modules`}
          icon={<ChartBar size={20} />}
        />
      </div>

      <h2>By Crew</h2>
      {data.crewBreakdown.length === 0 ? (
        <p>No crew data for the selected filters.</p>
      ) : (
        <CrewChart crewBreakdown={data.crewBreakdown} />
      )}

      <h2>Module Completion Heatmap</h2>
      {data.modules.length === 0 ? (
        <p>No module progress data yet.</p>
      ) : (
        <ModuleHeatmap modules={data.modules} />
      )}

      <h2>By Module</h2>
      {data.modules.length === 0 ? (
        <p>No progress data yet.</p>
      ) : (
        <div className="completion-bars">
          {data.modules.map((m) => (
            <CompletionBar
              key={m.moduleId}
              label={m.moduleId}
              value={m.completed}
              total={m.total}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function AdminProgressPage() {
  return (
    <FacilitatorGuard>
      <div className="admin-page">
        <h1>Progress Overview</h1>
        <p>Completion rates across crews, roles, and modules.</p>
        <AdminNav />
        <ProgressOverview />
      </div>
    </FacilitatorGuard>
  )
}
